<template>
  <div class="row">
    <div class="col-12">
      <custom-callout
        :has-toggle="false"
        :opened="open"
        border-color="gray-2"
        classMain="bg-white mt-2"
      >
        <div class="row py-2">
          <div class="col-12 col-md-2">
            <div class="info-text-title">Servicio:</div>
            <div class="info-text">
              {{ service }}
            </div>
          </div>

          <div
            class="col-12 col-md"
            v-if="isGeneralService || isAdditionalService"
          >
            <div class="info-text-title">Cantidad de meses:</div>
            <div class="info-text">
              {{ frequency }}
            </div>
          </div>

          <template v-if="canShowAdditionalFields">
            <div class="col-12 col-md">
              <div class="info-text-title">Frecuencia:</div>
              <div class="info-text">
                {{ therapyFrequency }}
              </div>
            </div>

            <div class="col-12 col-md">
              <div class="info-text-title">Modalidad:</div>
              <div class="info-text">
                {{ therapyServiceModality }}
              </div>
            </div>

            <div class="col-12 col-md">
              <div class="info-text-title">Duración:</div>
              <div class="info-text">
                {{ therapyDuration }}
              </div>
            </div>
          </template>

          <div class="col-12 col-md">
            <div class="info-text-title">{{ getCostLabel }}</div>
            <div class="info-text">
              {{ cost }}
            </div>
          </div>

          <div class="col-12 col-md" v-if="isGeneralService">
            <div class="info-text-title">Subtotal:</div>
            <div class="info-text">
              {{ subtotal }}
            </div>
          </div>

          <div class="col-12 col-md">
            <div class="info-text-title">Costo año anterior:</div>
            <div class="info-text" :class="[previousYearCost.color]">
              {{ previousYearCost.cost }}
            </div>
          </div>

          <template v-if="!readonly">
            <div class="col-12 col-md-2 my-auto text-right">
              <DropDown class="pr-2" :items="getActions()" title="Acciones" />
            </div>
          </template>
        </div>

        <div class="row py-2" v-if="hasComment">
          <div class="col-12">
            <div class="info-text-title">Comentario:</div>
            <div class="info-text">
              {{ formattedComment }}
            </div>
            <div class="info-text" v-if="model.commentCreatedBy">
              {{ model.commentCreatedBy }}
            </div>
          </div>
        </div>

        <template #edition>
          <AddEditServiceForm
            v-model="modelToEdit"
            :collections="collections"
            is-edit
            :costByServices="costByServices"
            :cost-readonly="costReadonly"
            :isAcceptBtnDisabled="isAcceptBtnDisabled"
            @on-service-change="onServiceChange"
            @cancel="close"
            @accept="save"
          />
        </template>
      </custom-callout>
      <div v-if="showAlertMessage" class="alert alert-warning" role="alert">
        <div class="alert-content">
          <i class="fas mr-2 fa-bell-exclamation" />
          <span>{{ this.model.peiValidationMessage }}</span>
        </div>
      </div>
      <div v-if="value?.isBillDue" class="alert alert-warning" role="alert">
        <div class="alert-content">
          <i class="fas mr-2 fa-bell-exclamation" />
          <span>
            Se identificó que para el periodo ya la institución tiene factura
            sometida.
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AddEditServiceForm from './AddEditServiceForm.vue';
import CustomCallout from 'custom-callout';
import DropDown from 'dropdown';
import { price } from '@/utils/filters.js';
import alert from '@/mixin/alert.mixin';
import centralLevelServices from '@/utils/constants/tapRequest/centralLevelServices.js';
import centralLevelServicesCategories from '@/utils/constants/tapRequest/centralLevelServicesCategories';
import {
  getServiceTotalCost,
  getServiceToShowBillFlag,
} from '@/services/api/tapRequest.api';

export default {
  name: 'ServiceRow',
  mixins: [alert],
  components: { AddEditServiceForm, CustomCallout, DropDown },
  props: {
    value: {
      type: Object,
      default: () => ({
        service: null,
        frequency: null,
        other: null,
        cost: null,
        costFromContract: null,
        subtotal: null,
        itinerantService: null,
        therapyFrequency: null,
        therapyServiceModality: null,
        therapyDuration: null,
        comment: null,
        commentCreatedAt: null,
        commentCreatedBy: null,
        peiValidationMessage: null,
      }),
    },
    collections: {
      type: Object,
      default: () => ({
        services: [],
        itinerantServices: [],
        servicesPreviousYear: [],
      }),
    },
    costByServices: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
    costReadonly: { type: Boolean, default: false },
    isAcceptBtnDisabled: { type: Boolean, default: false },
    index: { type: Number, default: 0 },
    academicYearId: { type: Number, default: null },
    schoolId: { type: Number, default: null },
  },
  data() {
    return {
      open: false,
      modelToEdit: null,
    };
  },
  computed: {
    model() {
      return this.value;
    },
    getCostLabel() {
      if (
        this.model.service?.categoryId ===
        centralLevelServicesCategories.GeneralServices
      ) {
        return 'Costo mensual:';
      } else if (
        this.model.service?.categoryId ===
        centralLevelServicesCategories.TherapyServices
      ) {
        return 'Tarifa por terapia:';
      } else if (
        this.model.service?.categoryId ===
        centralLevelServicesCategories.AditionalServices
      ) {
        return 'Costo del servicio:';
      }

      return 'Costo:';
    },
    service() {
      if (
        this.model.service &&
        this.model.service.id == centralLevelServices.Others
      ) {
        return `${this.model.service.name} - ${this.model.other}`;
      }

      return this.model.service?.name || '-';
    },
    isGeneralService() {
      return (
        this.model.service?.categoryId ===
        centralLevelServicesCategories.GeneralServices
      );
    },
    isTherapyService() {
      return (
        this.model.service?.categoryId ===
        centralLevelServicesCategories.TherapyServices
      );
    },
    isAdditionalService() {
      return (
        this.model.service?.categoryId ===
        centralLevelServicesCategories.AditionalServices
      );
    },
    itinerantService() {
      return `${this.model.itinerantService?.name || '-'}`;
    },
    canShowAdditionalFields() {
      return (
        this.model.service?.id === centralLevelServices.ABATherapy ||
        this.model.service?.id === centralLevelServices.EducationalTherapy
      );
    },
    frequency() {
      return this.model.frequency || '-';
    },
    cost() {
      return price(this.model.cost) || '-';
    },
    therapyFrequency() {
      return this.model.therapyFrequency?.name || '-';
    },
    therapyServiceModality() {
      return this.model.therapyServiceModality?.name || '-';
    },
    therapyDuration() {
      return this.model.therapyDuration?.name || '-';
    },
    subtotal() {
      const subtotal = this.model.cost * this.model.frequency;
      return price(subtotal) || '-';
    },
    previousYearCost() {
      var service = this.collections.servicesPreviousYear.find(
        (x) => x.service.id == this.model.service.id
      );

      var serviceCost = service ? service.cost : 0;

      var result = {
        cost: service ? price(service.cost) : '-',
        color:
          serviceCost > 0
            ? this.model.cost > serviceCost
              ? 'text-red'
              : this.model.cost == serviceCost
              ? 'text-green'
              : 'text-yellow'
            : '',
      };

      return result;
    },
    showAlertMessage() {
      return this.model.peiValidationMessage;
    },
    hasComment() {
      return this.model.comment && this.model.comment.trim() !== '';
    },
    formattedComment() {
      if (!this.hasComment) return '';

      const datePart = this.model.commentCreatedAt
        ? `${this.model.commentCreatedAt} - `
        : '';
      return `${datePart}${this.model.comment}`;
    },
  },
  methods: {
    onServiceChange(data) {
      this.$emit('on-service-change', data);
    },
    getActions() {
      return [
        {
          label: 'Editar',
          icon: 'fas fa-edit text-green pr-1',
          click: () => this.edit(),
        },
        {
          label: 'Eliminar',
          icon: 'fa fa-times-circle text-red pr-1',
          click: () => this.remove(),
        },
      ];
    },
    edit() {
      this.modelToEdit = { ...this.model };
      this.open = true;
    },
    async save(dto) {
      this.model.service = dto.service;
      this.model.frequency = dto.frequency;
      this.model.other = dto.other;
      this.model.cost = dto.cost;
      this.model.subTotal = +dto.subtotal;
      this.model.itinerantService = dto.itinerantService;
      this.model.therapyFrequency = dto.therapyFrequency;
      this.model.therapyServiceModality = dto.therapyServiceModality;
      this.model.therapyDuration = dto.therapyDuration;
      this.model.comment = dto.comment;
      this.model.commentCreatedAt = dto.commentCreatedAt;
      this.model.commentCreatedBy = dto.commentCreatedBy;
      this.model.isBillDue = false;
      if (dto.therapyFrequency?.id > 0) {
        const { data: subTotal } = await getServiceTotalCost({
          cost: dto.cost,
          frequencyType: dto.therapyFrequency?.id,
        });
        this.model.subTotal = subTotal;
      }
      const lookForIsDue = [
        centralLevelServices.MonthlyPayment,
        centralLevelServices.ServiceAssistant,
      ].includes(dto.service?.id);
      if (lookForIsDue && this.model.serviceDate) {
        const { data: isBillDue } = await getServiceToShowBillFlag(
          this.academicYearId,
          this.schoolId,
          new Date(this.model.serviceDate)
        );
        this.model.isBillDue = isBillDue;
      }
      this.ShowToast(
        'Confirmación',
        'El servicio se ha actualizado exitosamente.',
        'success'
      );

      this.close();
    },
    remove() {
      this.$emit('remove', this.value.service.id);
    },
    close() {
      this.open = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@import '@/assets/scss/variables/_colors.scss';

.info-text-title {
  color: map.get($colors, 'gray-6');
  font-size: 1rem;
  font-weight: normal;
}

.info-text {
  color: map.get($colors, 'black');
  font-weight: bold;
  font-size: 1rem;
  size: 1rem;
  overflow-wrap: break-word;
}

.text-yellow {
  color: #f8b539;
}

.text-red {
  color: #c9545e;
}

.alert-warning {
  border: none;
}

.alert-content {
  color: #af6708;
}
</style>
